<script setup lang="ts">
import { computed } from 'vue'
import { isUndefined, omitBy } from 'lodash'
import VsButton from '@/components/vision/VsButton.vue'
import VsDisclosure from '@/components/vision/VsDisclosure.vue'
import VsMenu from '@/components/vision/VsMenu.vue'
import NumberField from '@/components/vision/NumberField.vue'
import VsAutocomplete from '@/components/vision/Inputs/VsAutocomplete/VsAutocomplete.vue'
import VsTextInput from '@/components/vision/Inputs/VsTextInput.vue'
import VsDatePickerNext from '@/components/vision/next/inputs/VsDatePickerNext.vue'
import LineItemSelect from '@/components/jobs/line-items/LineItemSelect.vue'
import {
  type LineItemTypeDetailField,
  getDetailFieldsForType,
  isCustomLineItem,
  LINE_ITEM_TYPE_OPTIONS,
  resolveLineItemType,
  type WorkerOption,
} from '@/components/jobs/line-items/line-item-type'
import { LineItem, type LineItemDetails } from '@/models/line-item'
import PriceBookItem from '@/models/price-book-item'
import { PRICE_BOOK_ITEM_TYPES } from '@/api/types/priceBookTypes'
import { useCurrencyFormatter } from '@/utils/number-formatting'
import { useLineItemsTotal } from '@/components/jobs/line-items/useLineItemsTotal'
import {
  type LineItemFieldChanges,
  quantityRule,
  roundQuantityInput,
  roundRateInput,
} from '@/components/jobs/line-items/line-item-input'

interface Props {
  index: number
  lineItem: LineItem
  hubId: string
  assignedToServiceProviderId?: string | null
  priceBookItems?: PriceBookItem[]
  isLoadingPriceBook?: boolean
  /** Whether the user may see and edit rates/totals for this job. */
  showRates?: boolean
  canDuplicate?: boolean
  isBillable?: boolean
  /** Entitlement-gated billing controls: billable toggle, type, and "More details". */
  showBillingControls?: boolean
  /** The users the worker field offers. */
  workerOptions?: WorkerOption[]
  /** User uuid shown in the worker field until the user edits it. */
  defaultWorkerId?: string | null
  /** Shown in the work date field until the user picks a date. */
  defaultWorkDate?: Date | null
}

interface Emits {
  (e: 'update', changes: LineItemFieldChanges): void
  (e: 'remove-item'): void
  (e: 'duplicate-item'): void
  (e: 'toggle-billable'): void
}

const props = withDefaults(defineProps<Props>(), {
  assignedToServiceProviderId: null,
  priceBookItems: undefined,
  isLoadingPriceBook: false,
  showRates: false,
  canDuplicate: true,
  isBillable: true,
  showBillingControls: false,
  workerOptions: () => [],
  defaultWorkerId: null,
  defaultWorkDate: null,
})
const emit = defineEmits<Emits>()

const formatter = useCurrencyFormatter()

const lineItemId = computed(() => props.lineItem.id)

const matchedPriceBookItem = computed(() => {
  if (!props.lineItem.priceBookItemId || !props.priceBookItems) return null
  return props.priceBookItems.find((item) => item.id === props.lineItem.priceBookItemId) ?? null
})

const unitOfMeasure = computed(() => matchedPriceBookItem.value?.unitOfMeasure || undefined)

const displayedDescription = computed(
  () => props.lineItem.details.description ?? matchedPriceBookItem.value?.description ?? '',
)

// Shown even without billing controls: the item is still left out of the billable total.
const isNonBillable = computed(() => !props.isBillable)

const showTypeField = computed(() => props.showBillingControls && isCustomLineItem(props.lineItem))
// Linked items take their price book item's type; custom items the picked one.
const lineItemType = computed(() => resolveLineItemType(props.lineItem, props.priceBookItems))
const visibleDetailFields = computed(() =>
  props.showBillingControls ? getDetailFieldsForType(lineItemType.value) : [],
)
const showsDetail = (field: LineItemTypeDetailField) => visibleDetailFields.value.includes(field)

// Equipment calls its serial number a unit number.
const serialNumberLabel = computed(() =>
  lineItemType.value === PRICE_BOOK_ITEM_TYPES.AccessorialEquipmentHourly ||
  lineItemType.value === PRICE_BOOK_ITEM_TYPES.AccessorialEquipmentFlat
    ? 'Unit number'
    : 'Serial number',
)

const displayedWorkerId = computed(
  () => props.lineItem.details.workerId ?? props.defaultWorkerId ?? '',
)

/** Emits the item's details with `changes` applied; `undefined` removes a field. */
function updateDetails(changes: Partial<LineItemDetails>) {
  emit('update', {
    details: omitBy({ ...props.lineItem.details, ...changes }, isUndefined) as LineItemDetails,
  })
}

function updateWorker(selected: string | null) {
  // Clearing emits `null`; keep it as `''` so the default doesn't come back.
  const workerId = selected ?? ''
  if (workerId === displayedWorkerId.value) return
  updateDetails({ workerId })
}

const lineItemTotal = useLineItemsTotal(computed(() => [props.lineItem]))
const lineTotal = computed(() => {
  if (!props.lineItem.quantity || !props.lineItem.rate) return '—'
  return formatter.format(lineItemTotal.value)
})

const menuItems = computed(() => [
  {
    text: 'Duplicate item',
    dataTest: `line-item-duplicate-button-${lineItemId.value}`,
    disabled: !props.canDuplicate,
    // VsMenu still invokes onClick for disabled items, so omit it entirely.
    onClick: props.canDuplicate ? () => emit('duplicate-item') : undefined,
  },
  ...(props.showBillingControls
    ? [
        {
          text: props.isBillable ? 'Mark as non-billable' : 'Mark as billable',
          dataTest: `line-item-toggle-billable-button-${lineItemId.value}`,
          onClick: () => emit('toggle-billable'),
        },
      ]
    : []),
  { divider: true },
  {
    text: 'Delete item',
    dataTest: `line-item-delete-button-${lineItemId.value}`,
    destructive: true,
    onClick: () => emit('remove-item'),
  },
])

function selectPriceBookItem(value: {
  id: string | null
  defaultRate: number | null
  name: string
}) {
  if (value.id == null) {
    emit('update', { name: value.name, priceBookItemId: null })
  } else {
    emit('update', { name: value.name, priceBookItemId: value.id, rate: value.defaultRate })
  }
}
</script>

<template>
  <div
    class="line-item-form-card"
    :class="{ 'is-non-billable': isNonBillable }"
    data-test="line-item"
  >
    <div class="line-item-title">
      <div class="line-item-heading">Item {{ props.index }}</div>
      <transition name="status-tag">
        <div
          v-if="isNonBillable"
          class="status-tag"
          :data-test="`line-item-non-billable-${lineItemId}`"
        >
          <span class="status-tag-icon v-icon material-symbols-outlined" aria-hidden="true"
            >money_off</span
          >
          <span class="status-tag-label">Non-billable</span>
        </div>
      </transition>
      <div class="line-item-actions">
        <vs-menu :items="menuItems" horizontal-position="right">
          <template #activator>
            <vs-button
              icon="more_horiz"
              type="tertiary"
              small
              aria-label="Line item actions"
              :data-test="`line-item-actions-button-${lineItemId}`"
            />
          </template>
        </vs-menu>
      </div>
    </div>

    <div class="line-item-fields">
      <line-item-select
        :is-loading="props.isLoadingPriceBook"
        :available-price-book-items="props.priceBookItems"
        :value="props.lineItem.name"
        :assigned-to-service-provider-id="props.assignedToServiceProviderId"
        :hub-id="props.hubId"
        :data-test="`line-item-name-${lineItemId}`"
        @select-price-book-item="selectPriceBookItem"
      />
      <vs-autocomplete
        v-if="showTypeField"
        :value="props.lineItem.type"
        :items="LINE_ITEM_TYPE_OPTIONS"
        label="Line item type"
        :data-test="`line-item-type-${lineItemId}`"
        @input="(type) => emit('update', { type: type ?? null })"
      />
      <div class="line-item-amount">
        <number-field
          :value="props.lineItem.quantity"
          :data-test="`line-item-quantity-${lineItemId}`"
          label="Quantity"
          :suffix="unitOfMeasure"
          :min="0"
          step="any"
          :rules="[quantityRule]"
          required
          @input="(quantity) => emit('update', { quantity })"
          @blur="(quantity) => emit('update', { quantity: roundQuantityInput(quantity) })"
        />
        <div v-if="props.showRates" class="line-item-amount-rates">
          <number-field
            :value="props.lineItem.rate"
            :data-test="`line-item-rate-${lineItemId}`"
            label="Rate"
            prefix="$"
            :min="0"
            step="any"
            @input="(rate) => emit('update', { rate })"
            @blur="(rate) => emit('update', { rate: roundRateInput(rate) })"
          />
          <div
            class="line-item-total"
            :class="{ 'is-non-billable': isNonBillable }"
            :data-test="`line-item-total-${lineItemId}`"
          >
            {{ lineTotal }}
          </div>
        </div>
      </div>
    </div>

    <vs-disclosure v-if="props.showBillingControls">
      <template #summary>
        <div class="details-toggle-label">
          <span>More details</span>
        </div>
      </template>

      <div class="line-item-fields pt-4">
        <vs-text-input
          :value="displayedDescription"
          label="Description"
          multiline
          rows="2"
          :data-test="`line-item-description-${lineItemId}`"
          @input="(description) => updateDetails({ description })"
        />
        <vs-text-input
          v-if="showsDetail('serialNumber')"
          :value="props.lineItem.details.serialNumber ?? ''"
          :label="serialNumberLabel"
          :data-test="`line-item-serial-number-${lineItemId}`"
          @input="(serialNumber) => updateDetails({ serialNumber: serialNumber || undefined })"
        />
        <vs-text-input
          v-if="showsDetail('storageLocation')"
          :value="props.lineItem.details.storageLocation ?? ''"
          label="Storage location"
          :data-test="`line-item-storage-location-${lineItemId}`"
          @input="
            (storageLocation) => updateDetails({ storageLocation: storageLocation || undefined })
          "
        />
        <vs-autocomplete
          v-if="showsDetail('workerId')"
          :value="displayedWorkerId"
          :items="props.workerOptions"
          label="Worker"
          :data-test="`line-item-worker-${lineItemId}`"
          @input="updateWorker"
        />
        <vs-date-picker-next
          v-if="showsDetail('workDate')"
          :value="props.lineItem.details.workDate ?? props.defaultWorkDate"
          label="Work date"
          :enable-time="false"
          can-select-past
          :data-test="`line-item-work-date-${lineItemId}`"
          @input="(workDate) => updateDetails({ workDate: workDate ?? undefined })"
        />
        <vs-text-input
          :value="props.lineItem.details.referenceNumber ?? ''"
          label="Reference number"
          :data-test="`line-item-reference-number-${lineItemId}`"
          @input="
            (referenceNumber) => updateDetails({ referenceNumber: referenceNumber || undefined })
          "
        />
      </div>
    </vs-disclosure>
  </div>
</template>

<style scoped>
.line-item-form-card {
  --outer-shadow: 0 1px 0 0 rgb(0 0 0 / 15%);
  --inner-border: inset 0 0 0 1px var(--color-grey--lightest);

  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 16px;
  background: var(--color-white);
  border-radius: 8px;
  box-shadow: var(--outer-shadow), var(--inner-border);
  transition: box-shadow 200ms ease-out;

  &.is-non-billable {
    --inner-border: inset 0 0 0 2px #fec601;
  }
}

.line-item-title {
  display: flex;
  gap: 16px;
  align-items: center;
}

.line-item-heading {
  margin-right: auto;
  font-family: var(--font-display);
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
}

@media (prefers-reduced-motion: no-preference) {
  .status-tag-enter-active,
  .status-tag-leave-active {
    transition: opacity 200ms ease-out;
  }
}

.status-tag-enter,
.status-tag-leave-to {
  opacity: 0;
}

.line-item-fields {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.line-item-amount {
  display: flex;
  flex-wrap: wrap;
  flex: 1 1 auto;
  gap: 16px 8px;
  align-items: center;
}

.line-item-amount-rates {
  display: flex;
  flex: 1 1 auto;
  flex-wrap: wrap;
  gap: 16px 8px;

  > * {
    min-width: 0;
  }
}

/**
 * 1. 18px is a magic number in order to keep this centered with the quantity & rate fields.
 */
.line-item-total {
  align-self: flex-end;
  padding-bottom: 18px; /* [1] */
  margin-left: auto;
  font-family: var(--font-regular);
  font-size: 1.25rem;
  font-weight: 600;
  text-align: right;
  text-box: trim-both cap alphabetic;
  white-space: nowrap;

  &.is-non-billable {
    color: var(--color-text-disabled);
    text-decoration: line-through;
  }
}

.details-toggle-label {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 100%;
  padding-right: 8px;
  font-family: var(--font-regular);
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-blue--dark);
  user-select: none;

  > span {
    flex: 1 0 auto;
  }

  &::after {
    display: block;
    flex: 1 1 100%;
    height: 1px;
    content: '';
    background-color: rgb(0 0 0 / 12%);
  }
}

.status-tag {
  display: flex;
  gap: 6px;
  align-items: center;
  width: auto;
  padding: 5px 6px;
  background-color: var(--color-yellow--lighter);
  border-radius: 4px;
  box-shadow:
    inset 0 -1px 0 0 rgb(0 0 0 / 15%),
    inset 0 1px 0 0 rgb(255 255 255 / 60%);
}

.status-tag-icon {
  width: 0.875rem;
  height: 0.875rem;
  color: var(--color-black);

  &:has(> svg) {
    display: inline-flex;
  }
}

.status-tag-icon.v-icon {
  flex-shrink: 0;
  font-size: 0.875rem;
}

.status-tag-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  text-box: trim-both cap alphabetic;
  white-space: nowrap;
}
</style>
